import React, { ChangeEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { KeyValueItem } from '@gerdau/hefesto-mais-web';
import { Checkbox, Modal, RadioButton, TextField } from '@gerdau/hefesto';
import { ERoutes } from '../../../../../routes';
import { ApplicationState } from '../../../../../store';
import {
  saveMockup,
  setEditingMockup
} from '../../../../../store/ducks/fabrication-mockups/actions';
import {
  IFabricationMockup,
  IPavement
} from '../../../../../store/ducks/fabrication-mockups/types';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../store/ducks/toast/types';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { EProjectsMockupsRoutes } from '../../../projectsMockup.routes';
import NumberOfPavements from './NumberOfPavements';
import {
  MockupDataInputContainer,
  MockupRowContainer,
  OptionsContainer,
  SecondaryInputContainer,
  Subtitle
} from './styles';

interface IConfigMockupProps {
  allMockupNames: string[];
  mockup?: IFabricationMockup;
  contractNumber: string;
  onEdit?: (
    name: string,
    hasRoof: boolean,
    hasCovering: boolean,
    typicalFloorPlan: string,
    otherTypicalFloorPlan: string,
    pavements: IPavement[],
    id: number
  ) => void;
  onCancel: () => void;
}

enum ERoofAndCovering {
  ROOF = 'roof',
  COVERING = 'covering'
}

const ConfigMockupModal: React.FC<IConfigMockupProps> = ({
  allMockupNames,
  contractNumber,
  mockup,
  onEdit,
  onCancel
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isEdit] = useState(!!mockup);

  const NUMBER_MAX_OF_PAVEMENTS = 200;

  const { screenName, itemDescription } = getTaggingInformationScreen(
    isEdit ? 'modal-edit-mockup' : 'modal-add-mockup'
  );

  const saveMockupState = useSelector(
    (appState: ApplicationState) => appState.fabricationMockups.saveMockupState
  );

  const isLoading = useSelector(
    (appState: ApplicationState) =>
      appState.fabricationMockups.saveMockupState?.loading ||
      appState.fabricationMockups.loading
  );

  const [mockupName, setMockupName] = useState(mockup?.name || '');

  const [pavementsAboveGround, setPavementsAboveGround] = useState(
    mockup?.pavements.filter((p) => p.position >= 0).length || 1
  );

  const [pavementsBelowGround, setPavementsBelowGround] = useState(
    mockup?.pavements.filter((p) => p.position < 0).length || 0
  );

  const [typicalFloorPlan, setTypicalFloorPlan] = useState(
    mockup?.typicalFloorPlan || ''
  );
  const [hasTypicalFloorPlan, setHasTypicalFloorPlan] = useState(
    !!mockup?.typicalFloorPlan || false
  );

  const initRoofAndCoveringArray = (): ERoofAndCovering[] => {
    const value: ERoofAndCovering[] = [];
    if (mockup?.hasRoof) {
      value.push(ERoofAndCovering.ROOF);
    }

    if (mockup?.hasCovering) {
      value.push(ERoofAndCovering.COVERING);
    }
    return value;
  };
  const [roofAndCovering, setRoofAndCovering] = useState<ERoofAndCovering[]>(
    initRoofAndCoveringArray
  );
  const hasRoof = (): boolean =>
    roofAndCovering.includes(ERoofAndCovering.ROOF);
  const hasCovering = (): boolean =>
    roofAndCovering.includes(ERoofAndCovering.COVERING);

  const [hasOtherTypicalFloorPlan, setHasOtherTypicalFloorPlan] = useState(
    !!mockup?.otherTypicalFloorPlan || false
  );
  const [otherTypicalFloorPlan, setOtherTypicalFloorPlan] = useState(
    mockup?.otherTypicalFloorPlan || ''
  );

  const handleCreateMockup = () => {
    const pavements: IPavement[] = [];
    const defaultPavementName = typicalFloorPlan || 'Pavimento';

    const getPavementName = (pavementPosition: number): string => {
      if (pavementPosition > 0) {
        return `${defaultPavementName} ${pavementPosition}`;
      } else if (pavementPosition < 0) {
        return `Subsolo ${Math.abs(pavementPosition)}`;
      } else {
        return `Térreo`;
      }
    };

    for (let i = pavementsAboveGround - 1; i >= 0; i--) {
      pavements.push({ position: i, name: getPavementName(i) });
    }
    for (let i = 1; i <= pavementsBelowGround; i++) {
      pavements.push({ position: -i, name: getPavementName(-i) });
    }

    if (hasCovering()) {
      pavements.push({
        position: pavements.filter((p) => p.position > 0).length + 1,
        name: 'Cobertura'
      });
    }

    if (hasRoof()) {
      pavements.push({
        position: pavements.filter((p) => p.position > 0).length + 1,
        name: 'Coberta'
      });
    }

    pavements.sort((p1, p2) => p2.position - p1.position);

    dispatch(
      saveMockup(
        contractNumber,
        mockupName,
        hasRoof(),
        hasCovering(),
        typicalFloorPlan,
        otherTypicalFloorPlan,
        pavements,
        undefined,
        { origem: 'nova' }
      )
    );
  };

  const handleEditMockup = () => {
    if (!mockup || !onEdit) {
      return;
    }
    onEdit(
      mockupName,
      hasRoof(),
      hasCovering(),
      typicalFloorPlan,
      otherTypicalFloorPlan,
      mockup.pavements,
      mockup.id
    );
  };

  const canSaveMockup = (): boolean => {
    if (isEdit && mockup) {
      const { name: edtMockupName, typicalFloorPlan: edtTypicalFloorPlan } =
        mockup;
      return (
        (mockupName !== '' &&
          mockupName !== edtMockupName &&
          !allMockupNames.includes(mockupName)) ||
        edtTypicalFloorPlan !== typicalFloorPlan
      );
    }
    return (
      mockupName !== '' &&
      !allMockupNames.includes(mockupName) &&
      ((hasTypicalFloorPlan && typicalFloorPlan !== '') ||
        !hasTypicalFloorPlan) &&
      ((hasOtherTypicalFloorPlan && otherTypicalFloorPlan !== '') ||
        !hasOtherTypicalFloorPlan) &&
      pavementsAboveGround + pavementsBelowGround <= NUMBER_MAX_OF_PAVEMENTS
    );
  };

  const onChangeRoofAndCovering = (
    roofAndCoveringSelect: ERoofAndCovering,
    checked: boolean
  ) => {
    if (checked) {
      setRoofAndCovering([...roofAndCovering, roofAndCoveringSelect]);
      return;
    }
    const roofAndCoveringUpdate = [...roofAndCovering].filter(
      (type) => type !== roofAndCoveringSelect
    );
    setRoofAndCovering(roofAndCoveringUpdate);
  };

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName,
        event: 'page_view',
        page: history.location.pathname
      })
    );
  }, []);

  useEffect(() => {
    if (
      !isEdit &&
      !isLoading &&
      !!saveMockupState?.editingMockupId &&
      saveMockupState.success
    ) {
      history.push(
        `${ERoutes.PROJECT_MOCKUP}${EProjectsMockupsRoutes.MOCKUP_VIEW}`
      );
    }
  }, [
    isEdit,
    isLoading,
    saveMockupState?.editingMockupId,
    saveMockupState?.success,
    history
  ]);

  useEffect(() => {
    if (
      isEdit &&
      isLoading &&
      !!saveMockupState?.editingMockupId &&
      saveMockupState.success
    ) {
      onCancel();
    }
  }, [
    isEdit,
    isLoading,
    saveMockupState?.editingMockupId,
    saveMockupState?.success,
    onCancel
  ]);

  return (
    <Modal
      title={`${isEdit ? 'Editar' : 'Criar'} maquete`}
      onClose={() => onCancel()}
      buttons={{
        left: {
          name: 'cancelButton',
          type: 'button',
          children: 'Cancelar',
          onClick: () => {
            onCancel();
            dispatch(setEditingMockup(mockup?.id));
            dispatch(
              sendTagging({
                screenName,
                event: 'click',
                itemName: 'button:cancelar',
                itemDescription: `${itemDescription}_cancelar`
              })
            );
          },
          disabled: isLoading
        },
        right: {
          name: 'submitButton',
          type: 'button',
          children: `Concluir e visualizar maquete`,
          onClick: () => {
            isEdit ? handleEditMockup() : handleCreateMockup();
            dispatch(
              sendTagging({
                screenName,
                event: 'click',
                itemName: 'button:concluir_e_visualizar_maquete',
                itemDescription: `${itemDescription}_concluirEVisualizarMaquete`
              })
            );
          },
          loading: isLoading,
          disabled: !canSaveMockup() || isLoading
        }
      }}
    >
      {!isLoading &&
        saveMockupState?.error &&
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            durationInMS: 5000,
            message: <>Falha ao {`${isEdit ? 'editar' : 'criar'}`} maquete.</>
          })
        )}
      <Subtitle>
        Contrato: {`${contractNumber}${mockupName ? `- ${mockupName}` : ''}`}
      </Subtitle>
      <MockupRowContainer>
        <MockupDataInputContainer>
          <TextField
            label="Nome da maquete"
            name="newMockupInput"
            placeholder="Crie um nome para a maquete"
            value={mockupName}
            disabled={isLoading}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setMockupName(e.target.value)
            }
          />
        </MockupDataInputContainer>

        <MockupDataInputContainer>
          <TextField
            label="Total de pavimentos"
            name="totalPavementsInput"
            variant={
              pavementsAboveGround + pavementsBelowGround >
              NUMBER_MAX_OF_PAVEMENTS
                ? 'danger'
                : undefined
            }
            supportMessage={
              pavementsAboveGround + pavementsBelowGround >
              NUMBER_MAX_OF_PAVEMENTS
                ? 'O máximo de pavimentos é 200'
                : ''
            }
            placeholder={(
              pavementsBelowGround +
              pavementsAboveGround +
              (!isEdit && hasRoof() ? 1 : 0) +
              (!isEdit && hasCovering() ? 1 : 0)
            ).toString()}
            readOnly
          />
        </MockupDataInputContainer>
      </MockupRowContainer>
      <NumberOfPavements
        pavementsAboveGround={pavementsAboveGround}
        setPavementsAboveGround={setPavementsAboveGround}
        pavementsBelowGround={pavementsBelowGround}
        setPavementsBelowGround={setPavementsBelowGround}
        isEdit={isEdit}
        isLoading={isLoading}
      />
      <MockupRowContainer>
        <MockupDataInputContainer>
          <KeyValueItem
            title="A edificação possui pavimento tipo?"
            helperTextTooltipPlacement="right"
            helperText="Pavimento tipo é um ou mais pavimentos que se repetem em planta em um edifício."
          >
            {''}
          </KeyValueItem>
          <OptionsContainer>
            <RadioButton
              checked={hasTypicalFloorPlan}
              disabled={isLoading}
              value={'true'}
              label={'Sim'}
              id={'yesTypicalFloorPlanRadio'}
              name={'yesTypicalFloorPlanRadio'}
              onChange={() => {
                setHasTypicalFloorPlan(true);
              }}
            ></RadioButton>
            <RadioButton
              checked={!hasTypicalFloorPlan}
              disabled={isLoading}
              value={'false'}
              label={'Não'}
              id={'noTypicalFloorPlanRadio'}
              name={'noTypicalFloorPlanRadio'}
              onChange={() => {
                setHasTypicalFloorPlan(false);
                setTypicalFloorPlan('');
              }}
            ></RadioButton>
          </OptionsContainer>
          <div>
            {hasTypicalFloorPlan && (
              <SecondaryInputContainer>
                <TextField
                  name="typicalFloorPlanInput"
                  label="Qual nomenclatura desejada para esses lances?"
                  placeholder="Nome dos pavimentos tipo"
                  value={typicalFloorPlan}
                  disabled={isLoading}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setTypicalFloorPlan(e.target.value)
                  }
                />
              </SecondaryInputContainer>
            )}
          </div>
        </MockupDataInputContainer>
      </MockupRowContainer>
      {!isEdit && (
        <MockupRowContainer>
          <MockupDataInputContainer className="full-width">
            <KeyValueItem
              title="A edificação possui coberta ou cobertura?"
              helperTextTooltipPlacement="right"
              helperText="Selecione as opções de acordo com sua maquete."
            >
              {''}
            </KeyValueItem>
            <OptionsContainer>
              <Checkbox
                name={ERoofAndCovering.ROOF}
                checked={roofAndCovering.includes(ERoofAndCovering.ROOF)}
                label="Possui coberta"
                disabled={isLoading}
                onChange={(e) => {
                  onChangeRoofAndCovering(
                    ERoofAndCovering.ROOF,
                    e.target.checked
                  );
                }}
              ></Checkbox>
              <Checkbox
                name={ERoofAndCovering.COVERING}
                checked={roofAndCovering.includes(ERoofAndCovering.COVERING)}
                label="Possui cobertura"
                disabled={isLoading}
                onChange={(e) => {
                  onChangeRoofAndCovering(
                    ERoofAndCovering.COVERING,
                    e.target.checked
                  );
                }}
              ></Checkbox>
            </OptionsContainer>
          </MockupDataInputContainer>
        </MockupRowContainer>
      )}
      <MockupRowContainer>
        <MockupDataInputContainer>
          <KeyValueItem
            title=" Existe algum outro tipo de pavimento?"
            helperTextTooltipPlacement="right"
            helperText="Especifique este pavimento caso não seja um pavimento convencional."
          >
            {''}
          </KeyValueItem>
          <OptionsContainer>
            <RadioButton
              checked={hasOtherTypicalFloorPlan}
              disabled={isLoading || isEdit}
              value={'true'}
              label={'Sim'}
              id={'yesOtherTypicalFloorPlanRadio'}
              name={'yesOtherTypicalFloorPlanRadio'}
              onChange={() => {
                setHasOtherTypicalFloorPlan(true);
              }}
            ></RadioButton>
            <RadioButton
              checked={!hasOtherTypicalFloorPlan}
              disabled={isLoading || isEdit}
              value={'false'}
              label={'Não'}
              id={'noOtherTypicalFloorPlanRadio'}
              name={'noOtherTypicalFloorPlanRadio'}
              onChange={() => {
                setHasOtherTypicalFloorPlan(false);
                setOtherTypicalFloorPlan('');
              }}
            ></RadioButton>
          </OptionsContainer>
          {hasOtherTypicalFloorPlan && (
            <SecondaryInputContainer>
              <TextField
                name="otherTypicalFloorPlanInput"
                label="Especifique este pavimento"
                placeholder="Insira o nome e características"
                value={otherTypicalFloorPlan}
                disabled={isLoading}
                readOnly={isEdit}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setOtherTypicalFloorPlan(e.target.value)
                }
              />
            </SecondaryInputContainer>
          )}
        </MockupDataInputContainer>
      </MockupRowContainer>
    </Modal>
  );
};

export default ConfigMockupModal;
